import { trackError } from 'backend-client-web';
import {
  ClientCoreParameters,
  ExtensionAvailableInfoMessage,
  JsonFreezeCompatible,
  jsonParseThaw,
  jsonStringifyFreeze,
  PersistableClientCore,
} from 'persistable-types';
import { loadSymAesEncryptedBlob, toArrayBufferBacked } from 'vault-crypto';

export type LocalStorageEntryNoDefault<S> = {
  readonly key: string;
  readonly parse: (value: unknown) => JsonFreezeCompatible<S>;
  readonly transformBeforeStore?: (value: JsonFreezeCompatible<S>) => JsonFreezeCompatible<S>;
};

export type LocalStorageEntryWithDefault<S> = LocalStorageEntryNoDefault<S> & {
  readonly getDefault: () => JsonFreezeCompatible<S>;
};

export type LocalStorageEntry<S> = LocalStorageEntryNoDefault<S> | LocalStorageEntryWithDefault<S>;

export const LOCAL_STORAGE_KEYS = Object.freeze({
  email: {
    key: 'email',
    parse: parseAsString,
  },
  clientCoreParameters: {
    key: 'clientCoreParameters',
    parse: (d: unknown) => ClientCoreParameters.parse(d),
  },
  encryptedClientCoreParameters: {
    key: 'encryptedClientCoreParameters',
    parse: (d: unknown) => loadSymAesEncryptedBlob(parseAsUint8Array(d)),
  },
  /**
   * @deprecated PersistableClientCore is now stored encrypted. This unencrypted key is no longer
   * read or written; it is only retained so the legacy entry can be cleared on the next persist.
   * A client that had its persistable solely here (IDB unavailable) will fall back to an online
   * load / re-login.
   */
  persistableClientCore: {
    key: 'persistableClientCore',
    parse: (d: unknown) => PersistableClientCore.parse(d),
  },
  // PersistableClientCore is usually stored in IndexedDB, this key should only be used as fallback.
  fallbackEncryptedPersistableClientCore: {
    key: 'fallbackEncryptedPersistableClientCore',
    parse: parseAsUint8Array,
  },
  showLoggedIn: {
    key: 'showLoggedIn',
    parse: parseAsBoolean,
  },
  debugVisible: {
    key: 'debugVisible',
    parse: parseAsBoolean,
  },
  debugSimulateExtensionAvailable: {
    key: 'debugSimulateExtensionAvailable',
    parse: parseAsBoolean,
  },
  debugSilenceRequestLogging: {
    key: 'debugSilenceRequestLogging',
    parse: parseAsBoolean,
  },
  debugPaddleWebhookTargetUrl: {
    key: 'debugPaddleWebhookTargetUrl',
    parse: parseAsString,
  },
  hideRateExtension: {
    key: 'hideRateExtension',
    getDefault: () => false,
    parse: parseAsBoolean,
  },
  cachedExtensionInstalled: {
    key: 'cachedExtensionInstalled',
    parse: parseAsBoolean,
  },
  cachedExtensionAvailableInfoMessage: {
    key: 'cachedExtensionAvailableInfoMessage',
    parse: (es: unknown) => ExtensionAvailableInfoMessage.parse(es),
  },
  cachedHasLogins: {
    key: 'cachedHasLogins',
    parse: parseAsBoolean,
  },
  cachedNumberOfLogins: {
    key: 'cachedNumberOfLogins',
    parse: parseAsNumber,
  },
  referrerQuerystring: {
    key: 'referrerQuerystring',
    parse: parseAsString,
  },
  lastActivity: {
    key: 'lastActivity',
    /**
     * Use a default that is far in the past. This makes sure that the client is locked if this key
     * isn't present (e.g. if the user has set their browser to delete localStorage on close).
     *
     * On the first glance this may look like it would interfere with the pairing process as the key
     * also isn't present before pairing. But the auto lock logic uses the maximum of lastActivity
     * and unlockedAt, so this default doesn't hurt us.
     */
    getDefault: () => new Date(0),
    parse: parseDate,
  },
  menuWidth: {
    key: 'menuWidth',
    parse: parseAsNumber,
  },
  masterWidth: {
    key: 'masterWidth',
    parse: parseAsNumber,
  },
});

export function getLocalStorageEntry<S>(
  { key, parse }: LocalStorageEntry<S>,
  defaultValue: JsonFreezeCompatible<S>,
): JsonFreezeCompatible<S>;
export function getLocalStorageEntry<S>({
  key,
  parse,
}: LocalStorageEntryWithDefault<S>): JsonFreezeCompatible<S>;
export function getLocalStorageEntry<S>({
  key,
  parse,
}: LocalStorageEntryNoDefault<S>): JsonFreezeCompatible<S> | undefined;

export function getLocalStorageEntry<S>(
  { key, parse, ...rest }: LocalStorageEntry<S>,
  defaultValue?: JsonFreezeCompatible<S>,
): JsonFreezeCompatible<S> | undefined {
  let entry: string | undefined;
  try {
    entry = localStorage[key];
  } catch (e) {
    // Happens in Safari if "Block cookies" is enabled. Nothing we can do about it, simply fail
    // silently and behave as if local storage is empty.
    if (e instanceof DOMException && e.name === 'SecurityError') {
      entry = undefined;
    } else {
      throw e;
    }
  }
  if (!entry) {
    return defaultValue ?? ('getDefault' in rest ? rest.getDefault() : undefined);
  }
  try {
    const json = jsonParseThaw(entry);
    return parse(json);
  } catch (e) {
    trackError(new Error('Error reading from localStorage', { cause: e }));
    return defaultValue ?? ('getDefault' in rest ? rest.getDefault() : undefined);
  }
}

export function setLocalStorageEntry<S>(
  { key, transformBeforeStore }: LocalStorageEntry<S>,
  newValue: JsonFreezeCompatible<S> | undefined,
) {
  if (newValue === undefined) {
    try {
      delete localStorage[key];
    } catch (e) {
      // Happens in Safari if "Block cookies" is enabled. Nothing we can do about it, fail silently.
      if (e instanceof DOMException && e.name === 'SecurityError') {
        return;
      }
      throw e;
    }
  } else {
    const valueToStore = transformBeforeStore ? transformBeforeStore(newValue) : newValue;
    const stringifiedValue = jsonStringifyFreeze(valueToStore);
    try {
      localStorage[key] = stringifiedValue;
    } catch (e) {
      // Happens in Safari if "Block cookies" is enabled. Nothing we can do about it, fail silently.
      if (e instanceof DOMException && e.name === 'SecurityError') {
        return;
      }
      if (e instanceof DOMException && e.name === 'QuotaExceededError') {
        trackError(
          new Error(
            `Can't store to localStorage key "${key}" data of length ${stringifiedValue.length} (${stringifiedValue.length / 2 ** 20} MiB), quota exceeded`,
            { cause: e },
          ),
        );
      }
      throw e;
    }
  }
}

function parseAsString(str: unknown): string {
  if (typeof str === 'string') {
    return str;
  }
  throw new TypeError('Expected string');
}

function parseAsBoolean(b: unknown): boolean {
  if (typeof b === 'boolean') {
    return b;
  }
  throw new TypeError('Expected boolean');
}

function parseAsNumber(n: unknown): number {
  if (typeof n === 'number') {
    return n;
  }
  throw new TypeError('Expected number');
}

function parseDate(d: unknown): Date {
  if (d instanceof Date) {
    return d;
  }
  throw new TypeError('Expected string');
}

function parseAsUint8Array(arr: unknown): Uint8Array<ArrayBuffer> {
  if (arr instanceof Uint8Array) {
    return toArrayBufferBacked(arr);
  }
  throw new TypeError('Expected uint8Array');
}
